import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FichaForm } from '@/components/fichas/ficha-form'
import { createFicha, type CreateFichaBody } from '@/features/fichas/api'
import { fichasCopy } from '@/features/fichas/copy'
import { isPublishableFicha, toContentBody } from '@/features/fichas/content'
import { createEmptyFicha, fichaContentSchema, type FichaCata } from '@escuela/ficha-domain'

export function FichaNewPage(): ReactNode {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [ficha, setFicha] = useState<FichaCata>(() => createEmptyFicha())

  const createMutation = useMutation({
    mutationFn: (body: CreateFichaBody) => createFicha(body),
    onSuccess: () => {
      toast.success(fichasCopy.toasts.created)
      void queryClient.invalidateQueries({ queryKey: ['fichas'] })
      navigate('/dashboard/fichas')
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : fichasCopy.toasts.createError)
    },
  })

  const content = useMemo(() => toContentBody(ficha), [ficha])

  const handleSave = (): void => {
    const parsed = fichaContentSchema.safeParse(content)
    if (!parsed.success) {
      toast.error(fichasCopy.toasts.invalid)
      return
    }
    // El borrador se puede guardar parcial, pero avisamos si todavia no cumple
    // lo minimo para poder publicarlo mas adelante.
    if (!isPublishableFicha(ficha)) {
      toast.warning(fichasCopy.toasts.draftIncomplete)
    }
    createMutation.mutate({ id: ficha.id, ...content })
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{fichasCopy.newTitle}</h1>
        <p className="text-sm text-muted-foreground">{fichasCopy.newDescription}</p>
      </div>

      <FichaForm value={ficha} onChange={setFicha} />

      <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t bg-background/95 py-4 backdrop-blur">
        <Button variant="outline" onClick={() => navigate('/dashboard/fichas')} disabled={createMutation.isPending}>
          {fichasCopy.actions.cancel}
        </Button>
        <Button onClick={handleSave} disabled={createMutation.isPending}>
          {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {fichasCopy.actions.saveDraft}
        </Button>
      </div>
    </div>
  )
}
